import {
  Button,
  Col,
  DateRangePickerField,
  Flex,
  Form,
  Row,
  SelectField,
  SubmitButton,
  TextInputField,
} from '@/components';
import { TagSelectField } from '@/features/tag/components';
import { tagType } from '@/features/tag/models';
import { useHierarchyLabels } from '@/features/tenant-manager/hierarchy-label/_hooks/hierarchy-label';
import {
  ActionRequiredLabelModifier,
  type WorkflowSearchFormSchema,
  WorkflowStatusLabelModifier,
  actionRequiredEnum,
  workflowStatus,
} from '@/features/workflow/models/search';
import type { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { WorkflowListSearchFilterTags } from './WorkflowListSearchFilterTags';

const workflowStatusOptions = workflowStatus.options.map((value) => ({
  value,
  label: WorkflowStatusLabelModifier[value],
}));

const actionRequiredOptions = actionRequiredEnum.options.map((value) => ({
  value,
  label: ActionRequiredLabelModifier[value],
}));

type FormValues = z.infer<typeof WorkflowSearchFormSchema>;

type Props = {
  onSearch: (values: FormValues) => void;
  resetFormValues: FormValues;
};

export const WorkflowListSearchForm: FC<Props> = ({
  onSearch,
  resetFormValues,
}) => {
  const { t } = useTranslation();

  const { data: hierarchyLabelOptions } = useHierarchyLabels({
    params: { enabledOnly: true },
    select: (data) =>
      data.map((label) => ({
        value: label.id,
        label: label.name,
      })),
  });

  const {
    control,
    formState: { errors },
    reset,
    handleSubmit,
  } = useFormContext<FormValues>();

  return (
    <Form
      leavePageAlertCondition={null}
      layout="vertical"
      onFinish={handleSubmit(onSearch)}
    >
      <Row gutter={[16, 0]}>
        <Col span={6}>
          <DateRangePickerField
            name="createdMonthRange"
            label={t('created-month')}
            control={control}
            errors={errors}
            picker="month"
            allowEmpty={[true, true]}
            fieldStyle={{ width: '100%' }}
          />
        </Col>
        <Col span={6}>
          <TagSelectField
            name="tagIds"
            control={control}
            errors={errors}
            tagMaxWidth={120}
            type={tagType.Values.workflow}
          />
        </Col>
        <Col span={6}>
          <TextInputField
            name="keyword"
            autocompleteKey="workflow.keyword"
            label={t('keyword')}
            hint={t('workflow.hint.search-keyword')}
            control={control}
            errors={errors}
          />
        </Col>
        <Col span={6}>
          <SelectField
            name="actionRequired"
            label={t('workflow.is-action-required')}
            control={control}
            errors={errors}
            options={actionRequiredOptions}
            allowClear={false}
          />
        </Col>
        <Col span={6}>
          <SelectField
            name="stageLabelId"
            control={control}
            errors={errors}
            options={hierarchyLabelOptions}
            label={t('workflow.current-stage-label')}
            useNotInOption
          />
        </Col>
        <Col span={6}>
          <SelectField
            name="workflowStatus"
            control={control}
            errors={errors}
            options={workflowStatusOptions}
            label={t('workflow.status-search-label')}
            allowClear={false}
          />
        </Col>
      </Row>
      <Row style={{ marginBottom: 16 }}>
        <Col span={24}>
          <WorkflowListSearchFilterTags />
        </Col>
      </Row>
      <Flex gap={8} align="center">
        <Button
          htmlType="button"
          type="default"
          onClick={() => {
            reset(resetFormValues);
          }}
        >
          {t('search-clear')}
        </Button>
        <SubmitButton text={t('search-by')} />
      </Flex>
    </Form>
  );
};
